import { createClient } from "@connectrpc/connect";
import {
  createTankClient,
  createTankTransport,
  IndexedDbStorage,
  type TankClient,
} from "@tactical-agent-neural-knowledge/sdk";
import { AuthService } from "@tactical-agent-neural-knowledge/sdk/contracts/auth";
import { BillingService } from "@tactical-agent-neural-knowledge/sdk/contracts/billing";
import { CatalogService } from "@tactical-agent-neural-knowledge/sdk/contracts/catalog";
import { EnvironmentService } from "@tactical-agent-neural-knowledge/sdk/contracts/environment";
import { FilesService } from "@tactical-agent-neural-knowledge/sdk/contracts/files";
import { PlatformService } from "@tactical-agent-neural-knowledge/sdk/contracts/platform";
import { WorkspaceService } from "@tactical-agent-neural-knowledge/sdk/contracts/workspace";
import { reportSendFailed } from "./clientReport";
import { API_URL, AUTH_MODE, WS_URL } from "./env";
import { tokens } from "./tokens";

// A transport without credentials, used only to rotate the refresh token in bearer mode.
const bareAuth = createClient(
  AuthService,
  createTankTransport({ baseUrl: API_URL, auth: { bearer: async () => "" } }),
);

let refreshing: Promise<string> | undefined;

async function refreshAccess(): Promise<string> {
  const refreshToken = tokens.refresh();
  if (!refreshToken) return "";
  try {
    const res = await bareAuth.refresh({ refreshToken });
    tokens.set({
      accessToken: res.accessToken,
      refreshToken: res.refreshToken,
      accessExpiresAtMs: res.accessExpiresAt ? Number(res.accessExpiresAt.seconds) * 1000 : undefined,
    });
    return res.accessToken;
  } catch {
    tokens.clear();
    return "";
  }
}

/** Bearer for every request in dev builds; refreshes ahead of expiry, single-flight. */
export async function bearer(): Promise<string> {
  if (!tokens.accessStale()) return tokens.access();
  if (!refreshing) refreshing = refreshAccess().finally(() => (refreshing = undefined));
  return refreshing;
}

export const tankAuth = AUTH_MODE === "cookie" ? ("cookie" as const) : { bearer };

const transport = createTankTransport({ baseUrl: API_URL, auth: tankAuth });

/** Service clients used before a TankClient exists (login, magic link, GetMe, workspaces, logout). */
export const authApi = createClient(AuthService, transport);
export const workspaceApi = createClient(WorkspaceService, transport);
/**
 * The catalog, bound to no account. Browsing is anonymous: somebody has to be able
 * to look at what is for sale before they have somewhere to sign in to. The
 * per-account client below is what a watch list or a portfolio goes through.
 */
export const catalogApi = createClient(CatalogService, transport);

/**
 * Services graded by the world: environments, their truth, scores and past predictions.
 * Public, like the catalog.
 */
export const environmentApi = createClient(EnvironmentService, transport);

/**
 * `actingUserId` names which of the device's accounts this client speaks for, and is left off when
 * the device holds only one. A header the server does not allow is refused by the browser before
 * the request is made, so sending one that changes nothing only widens what a CORS mistake can
 * break — and a preflight, once cached, cannot be undone from the server.
 */
export function createClientForUser(userId: string, actingUserId?: string): TankClient {
  const client = createTankClient({
    baseUrl: API_URL,
    wsUrl: WS_URL,
    auth: tankAuth,
    // Each account gets its own IndexedDB or they would overwrite each other's cached Treads.
    ...(actingUserId ? { actAsUserId: actingUserId } : {}),
    storage: new IndexedDbStorage({ name: `tank-${userId}` }),
  });
  // A message that did not send says why on the server too, not only under the message.
  client.events.on("sendFailed", (e) => reportSendFailed(e.error));
  return client;
}

/** Service clients bound to one of the device's accounts, for calls made outside a TankClient. */
export function apisForUser(userId: string) {
  const transport = createTankTransport({ baseUrl: API_URL, auth: tankAuth, actAsUserId: userId });
  return {
    auth: createClient(AuthService, transport),
    workspaces: createClient(WorkspaceService, transport),
    // The console above every workspace. It has to speak for one of the device's
    // accounts like everything else: a device with two signed in cannot be asked
    // which session to use without being told, and the server answers "sign in".
    platform: createClient(PlatformService, transport),
    // Watch lists and portfolios belong to one of the device's accounts, so they
    // travel the same way the console does rather than through the anonymous client.
    catalog: createClient(CatalogService, transport),
    // A person's service subscriptions, and the upcoming predictions they unlock.
    billing: createClient(BillingService, transport),
    environment: createClient(EnvironmentService, transport),
    // A workspace icon under another email can only be read as that account.
    files: createClient(FilesService, transport),
  };
}
